import api from "../../lib/api";
import { LoaderFunctionArgs, redirect, useLoaderData, useNavigation, useSubmit } from "react-router-dom";
import { RuleSet, RuleSetEditorData, RuleSetMetadataEntry } from "../../types/ruleSetTypes";
import { Box, Button, Paper, TextField } from "@mui/material";
import PageHeader from '../../components/PageHeader';
import './styles.css';
import { useEffect, useState } from "react";
import { convertToQueryBuilderRuleGroup, convertToRuleSet } from "./converters";
import { RuleSetAccordion } from "../../components/RuleSetAccordion";
import { useTranslation } from 'react-i18next';

export async function ruleSetEditorLoader({ params }: LoaderFunctionArgs) {
  // If we are loading the component for editing an existing ruleset, then ruleSetId
  // will be defined in the params.  Otherwise we are creating a new ruleset.

  const metadataPromise = api.get<RuleSetMetadataEntry[]>('/v1/rulesets/metadata');

  // If we are editing an existing ruleset, fetch the ruleset.
  if (params.ruleSetId) {
    const [metadata, ruleSet] = await Promise.all([
      metadataPromise,
      api.get<RuleSet>(`/v1/rulesets/${params.ruleSetId}`),
    ]);
    return { metadata, ruleSet };
  }

  const metadata = await metadataPromise;
  return { metadata };
}

export async function ruleSetCreateAction({ request }: LoaderFunctionArgs) {
  const ruleSet = await request.json() as RuleSet;
  const result = await api.post<{ id: string }>('/v1/rulesets', ruleSet);
  return redirect(`/rules/${result.id}`);
}

export async function ruleSetEditAction({ request }: LoaderFunctionArgs) {
  const ruleSet = await request.json() as RuleSet;
  const result = await api.put<{ id: string }>(`/v1/rulesets/${ruleSet.id}`, ruleSet);
  return redirect(`/rules/${result.id}`);
}

export default function RuleSetEditor() {
  const { t } = useTranslation();
  const { metadata, ruleSet } = useLoaderData() as { metadata: RuleSetMetadataEntry[], ruleSet?: RuleSet };
  const navigation = useNavigation();
  const submit = useSubmit();

  const [editorData, setEditorData] = useState<RuleSetEditorData>({
    ruleSetId: ruleSet?.id,
    name: ruleSet?.name ?? "",
    description: ruleSet?.description ?? "",
    acceptQuery: convertToQueryBuilderRuleGroup(ruleSet?.acceptRules),
    rejectQuery: convertToQueryBuilderRuleGroup(ruleSet?.rejectRules),
    reviewQuery: convertToQueryBuilderRuleGroup(ruleSet?.reviewRules),
    concurrentModificationGuard: ruleSet?.concurrentModificationGuard
  });

  // Update editorData whenever ruleSet changes
  useEffect(() => {
    setEditorData({
      ruleSetId: ruleSet?.id,
      name: ruleSet?.name ?? "",
      description: ruleSet?.description ?? "",
      acceptQuery: convertToQueryBuilderRuleGroup(ruleSet?.acceptRules),
      rejectQuery: convertToQueryBuilderRuleGroup(ruleSet?.rejectRules),
      reviewQuery: convertToQueryBuilderRuleGroup(ruleSet?.reviewRules),
      concurrentModificationGuard: ruleSet?.concurrentModificationGuard
    });
  }, [ruleSet]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const ruleSet = convertToRuleSet(editorData);

    if (ruleSet.id) {
      submit(ruleSet, {
        method: "PUT",
        encType: "application/json",
        action: `/rules/${ruleSet.id}`});
    }
    else {
      submit(ruleSet, {
        method: "POST",
        encType: "application/json",
        action: "/rules/create"});
    }
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, '& > :first-child': { mb: 1 } }}>
      <PageHeader title={editorData.name || t('rules.create')} description={t('ui.rulesDescription')} />
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <TextField
          required label={t('common.fields.name')}
          variant="outlined"
          fullWidth
          onChange={ (event) => setEditorData({ ...editorData, name: event.target.value }) }
          defaultValue={editorData.name}
        />
      </Box>
      <Box>
        <TextField
          label={t('common.fields.description')}
          variant="outlined"
          fullWidth
          onChange={ (event) => setEditorData({ ...editorData, description: event.target.value }) }
          defaultValue={editorData.description}
        />
      </Box>
      </Paper>
      <Box>
        <RuleSetAccordion
          title={t('rules.acceptWhen')}
          color={"MediumSeaGreen"}
          ruleGroup={editorData.acceptQuery}
          readonly={false}
          metadata={metadata}
          onQueryChange={ (query) => setEditorData({ ...editorData, acceptQuery: query })}
        />
        <RuleSetAccordion
          title={t('rules.rejectWhen')}
          color={"Tomato"}
          ruleGroup={editorData.rejectQuery}
          readonly={false}
          metadata={metadata}
          onQueryChange={ (query) => setEditorData({ ...editorData, rejectQuery: query }) }
        />
        <RuleSetAccordion
          title={t('rules.reviewWhen')}
          color={"Orange"}
          ruleGroup={editorData.reviewQuery}
          metadata={metadata}
          readonly={false}
          onQueryChange={ (query) => setEditorData({ ...editorData, reviewQuery: query }) }
        />
      </Box>
      <Box>
        <Button
          disabled={navigation.state !== 'idle' || !editorData.name.trim()}
          variant="contained"
          size="large"
          type="submit"
          onClick={handleSubmit}>
            {t('common.actions.saveChanges')}
        </Button>
      </Box>
    </Box>
  )
}
